import {
  Controller,
  useFormContext,
  type FieldValues,
  type Path,
} from 'react-hook-form'

import { Select } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'

type DukunganTindakLanjutSelectProps<
  TFormValues extends FieldValues = FieldValues
> = {
  isReadOnly?: boolean
  isDisabled?: boolean
  isRequired?: boolean
  options: Array<string>
  fieldName: Path<TFormValues>
}

const OTHER_VALUE = '__OTHER__'

function DukunganTindakLanjutSelect<
  TFormValues extends FieldValues = FieldValues
>({
  isReadOnly = false,
  isDisabled = false,
  isRequired = false,
  options,
  fieldName,
}: DukunganTindakLanjutSelectProps<TFormValues>) {
  const formContext = useFormContext<TFormValues>()
  if (!formContext) {
    throw new Error(
      'DukunganTindakLanjutSelect must be used within `<FormProvider />`'
    )
  }

  const { control } = formContext

  return (
    <Controller<TFormValues>
      control={control}
      name={fieldName}
      rules={{
        validate: (value) => {
          // Bypass validation if the field is not required
          if (!isRequired) return true

          // If the value is empty, it means that no options is selected
          // Hence, return error message for select element
          if (!value) return 'Wajib dipilih'

          // If the value is OTHER_VALUE, it means that "Lainnya" is selected
          // but the text input is still empty.
          // Hence, return error message for text input element
          if (value === OTHER_VALUE) return 'Wajib diisi'

          return true
        },
      }}
      render={({
        field: { onChange, value },
        fieldState: { error, invalid },
      }) => {
        const hasNoInitialValue = value === undefined || value === null

        const isInOptions = options.includes(value)

        // If there is no initial value, show the empty option
        // If the value is in the options, show the value
        // If the value is not in the options, show the OTHER_VALUE
        const selectValue = hasNoInitialValue
          ? ''
          : isInOptions
          ? value
          : OTHER_VALUE

        // Strip OTHER_VALUE from the field value so its not included in the input value
        const inputValue = value?.replace(OTHER_VALUE, '')

        // Whether "Lainnya" is selected
        const isLainnyaSelected = !hasNoInitialValue && !isInOptions

        return (
          <fieldset className="space-y-2">
            <Text variant="heading-sm">
              Apa dukungan yang Anda butuhkan untuk melakukan upaya tindak
              lanjut?
            </Text>
            {isReadOnly ? (
              <div className="space-y-1 max-w-full lg:w-4/6">
                <Text color="subdued" aria-hidden={true}>
                  Kebutuhan Dukungan (Apa/Siapa):
                </Text>
                <Text className="overflow-wrap-anywhere" aria-hidden="true">
                  {isLainnyaSelected ? inputValue : value}
                </Text>

                <span className="sr-only">
                  Kebutuhan Dukungan (Apa/Siapa):{' '}
                  {isLainnyaSelected ? inputValue : selectValue}
                </span>
              </div>
            ) : (
              <>
                <div className="space-y-1 max-w-full lg:w-4/6">
                  <Text
                    as="label"
                    htmlFor={`${fieldName}Dropdown`}
                    color="subdued"
                  >
                    Kebutuhan Dukungan (Apa/Siapa):
                  </Text>
                  <div>
                    <Select
                      id={`${fieldName}Dropdown`}
                      placeholder="Pilih di sini"
                      className="block overflow-wrap-anywhere whitespace-break-spaces"
                      isDisabled={isDisabled}
                      value={isLainnyaSelected ? OTHER_VALUE : selectValue}
                      isInvalid={isLainnyaSelected ? false : invalid}
                      errorMessage={isLainnyaSelected ? '' : error?.message}
                      onChange={(event) => onChange(event.target.value)}
                    >
                      <option
                        disabled
                        className="text-disabled"
                        selected
                        value=""
                      >
                        Pilih di sini
                      </option>
                      {options.map((item, i) => (
                        <option key={i} value={item}>
                          {item}
                        </option>
                      ))}
                      <option value={OTHER_VALUE}>Lainnya</option>
                    </Select>
                  </div>
                </div>

                {isLainnyaSelected && (
                  <div className="space-y-1 max-w-full lg:w-4/6">
                    <Text
                      as="label"
                      htmlFor={`${fieldName}TextArea`}
                      color="subdued"
                    >
                      Kebutuhan Dukungan (Apa/Siapa): Lainnya
                    </Text>

                    <Textarea
                      isDisabled={isDisabled}
                      name={`${fieldName}TextArea`}
                      value={inputValue}
                      onChange={(event) => onChange(event.target.value)}
                      id={`${fieldName}TextArea`}
                      placeholder="Contoh: Penggunaan ruang kelas untuk praktik simulasi."
                      maxLength={250}
                      rows={3}
                      isInvalid={invalid}
                      errorMessage={error?.message}
                    />
                  </div>
                )}
              </>
            )}
          </fieldset>
        )
      }}
    />
  )
}
export default DukunganTindakLanjutSelect
